import axios from 'axios';
import type {
    SiteSettings,
    PaginatedResponse,
    AdminCreateUserDto,
    AdminUpdateUserDto,
    AdminUserDetailDto,
    StrikeDto,
    AdminCreateOrderDto,
    AdminCreateProductDto,
    AdminOrderDetailDto,
    AdminProductDetailDto,
    AdminUpdateOrderDto,
    AdminUpdateProductDto,
    CategoryDto,
    CreateSpecificationDto,
    SpecificationDto,
    UpdateSpecificationDto,
    AdminUserListDto,
    AdminProductListDto,
    AdminOrderListDto,
    CreateCategoryDto,
    UpdateCategoryDto,
    AdminChecklistTemplate,
    AdminCreateChecklistTemplateDto,
    AdminUpdateChecklistTemplateDto,
    AdminChecklistPhaseDto,
    ShopifyWebhookStatus,
    CreateTranslationDto,
    TranslationAdminDto,
    UpdateTranslationDto,
    LanguageAdminDto,
    AdminSidebarFilterListDto,
    SidebarFilterDto,
    CreateSidebarFilterDto,
    UpdateSidebarFilterDto,
    AdminMyRebabyPhaseListDto,
    AdminMyRebabyPhaseDetailDto,
    AdminUpdateMyRebabyPhaseContentDto,
    AdminTicketListDto,
    PlatformFeeTierDto,
    UpsertPlatformFeeTierDto,
    PlatformFeeRevenueDto,
    PlatformFeePreviewDto,
    MarketplaceEconomicsDto,
} from '../types';

// Runtime guard removed (separation now enforced via explicit imports in shop vs admin code)

export const adminAxios = axios.create({
    baseURL: '/api',
    withCredentials: true
});

// Admin API Calls
export const adminLogout = async (): Promise<void> => {
    await adminAxios.post('/admin/auth/logout');
};

/** Admin branding only — never economics. */
export const updateSiteSettings = async (settings: SiteSettings): Promise<void> => {
    await adminAxios.put('/admin/sitesettings', settings);
};

export const getSiteSettingsAdmin = async (): Promise<SiteSettings> => {
    const response = await adminAxios.get('/admin/sitesettings');
    return response.data;
};

export interface AdminLoginPromptSettings {
    gapMinutes: number;
    entries: { promptId: string; sortOrder: number; enabled: boolean }[];
}

/** Admin-only order and gap. Shop reads /login-prompts/queue. */
export const getAdminLoginPromptSettings = async (): Promise<AdminLoginPromptSettings> => {
    const response = await adminAxios.get<AdminLoginPromptSettings>('/admin/login-prompts');
    return response.data;
};

export const updateAdminLoginPromptSettings = async (settings: AdminLoginPromptSettings): Promise<void> => {
    await adminAxios.put('/admin/login-prompts', settings);
};

/** Admin marketplace economics (tax / shipping / platform fee toggles). */
export const getMarketplaceEconomicsAdmin = async (): Promise<MarketplaceEconomicsDto> => {
    const response = await adminAxios.get('/admin/marketplace-economics');
    return response.data;
};

export const updateMarketplaceEconomicsAdmin = async (
    data: MarketplaceEconomicsDto
): Promise<void> => {
    await adminAxios.put('/admin/marketplace-economics', data);
};

export type PickupLocationAdminDto = {
    id: number;
    city: string;
    region?: string | null;
    isActive: boolean;
    sortOrder: number;
};

export const getPickupLocationsAdmin = () =>
    adminAxios.get<PickupLocationAdminDto[]>('/admin/pickup-locations').then((r) => r.data);
export const createPickupLocationAdmin = (body: Omit<PickupLocationAdminDto, 'id'>) =>
    adminAxios.post<PickupLocationAdminDto>('/admin/pickup-locations', body).then((r) => r.data);
export const updatePickupLocationAdmin = (id: number, body: Omit<PickupLocationAdminDto, 'id'>) =>
    adminAxios.put<PickupLocationAdminDto>(`/admin/pickup-locations/${id}`, body).then((r) => r.data);
export const deletePickupLocationAdmin = (id: number) =>
    adminAxios.delete(`/admin/pickup-locations/${id}`);

export const getAllUsers = (params: { page?: number; limit?: number } = { page: 1, limit: 20 }) =>
    adminAxios.get<PaginatedResponse<AdminUserListDto>>('/admin/users', { params }).then(res => res.data);

export const getAllOrders = (params: { page?: number; limit?: number } = { page: 1, limit: 20 }) =>
    adminAxios.get<PaginatedResponse<AdminOrderListDto>>('/admin/orders', { params }).then(res => res.data);

export const getAllItems = (params: { page?: number; limit?: number } = { page: 1, limit: 20 }) =>
    adminAxios.get<PaginatedResponse<AdminProductListDto>>('/admin/products', { params }).then(res => res.data);

export const deleteUser = (id: string): Promise<void> =>
    adminAxios.delete(`/admin/users/${id}`).then(() => { });

export const deleteOrder = (id: number): Promise<void> =>
    adminAxios.delete(`/admin/orders/${id}`).then(() => { });

export const deleteProduct = (id: number): Promise<void> =>
    adminAxios.delete(`/admin/products/${id}`).then(() => { });

export const getProductById = (id: number) => adminAxios.get<AdminProductDetailDto>(`/admin/products/${id}`).then(res => res.data);

export const createProductAdmin = (data: AdminCreateProductDto) =>
    adminAxios.post<AdminProductDetailDto>('/admin/products', data)
        .then(res => res.data);

export const updateProductAdmin = (id: number, data: AdminUpdateProductDto) =>
    adminAxios.put<AdminProductDetailDto>(`/admin/products/${id}`, data)
        .then(res => res.data);

export const uploadAdminProductImages = async (files: File[]): Promise<string[]> => {
    if (!files.length) return [];
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    const response = await adminAxios.post('/admin/images/upload', formData);
    return response.data;
};

export const getAdminOrderById = (id: number) => adminAxios.get<AdminOrderDetailDto>(`/admin/orders/${id}`).then(res => res.data);

export const createOrderAdmin = (model: AdminCreateOrderDto) => adminAxios.post<AdminOrderDetailDto>('/admin/orders', model).then(res => res.data);

export const updateOrderAdmin = (id: number, model: AdminUpdateOrderDto) => adminAxios.put<AdminOrderDetailDto>(`/admin/orders/${id}`, model).then(res => res.data);

export const getUserById = (id: string) => adminAxios.get<AdminUserDetailDto>(`/admin/users/${id}`).then(res => res.data);

export const createUser = (model: AdminCreateUserDto) => adminAxios.post('/admin/users', model).then(res => res.data);

export const updateUser = (id: string, model: AdminUpdateUserDto) => adminAxios.put(`/admin/users/${id}`, model).then(res => res.data);

export const addStrike = (id: string, model: StrikeDto) => adminAxios.post(`/admin/users/${id}/strikes`, model).then(res => res.data);

export const removeStrike = (id: string, strikeId: string) => adminAxios.delete(`/admin/users/${id}/strikes/${strikeId}`).then(res => res.data);

export const banUser = (id: string, reason?: string) => adminAxios.post(`/admin/users/${id}/ban`, { reason }).then(res => res.data);

export const unbanUser = (id: string) => adminAxios.post(`/admin/users/${id}/unban`).then(res => res.data);

/** Admin bypass: set shop EmailConfirmed so login works without the confirm link. */
export const setUserEmailConfirmed = (id: string, confirmed: boolean) =>
    adminAxios.put(`/admin/users/${id}/email-confirmed`, { confirmed }).then(res => res.data);

export const forceConfirmUserEmail = (id: string) =>
    adminAxios.post(`/admin/users/${id}/force-confirm-email`).then(res => res.data);

export const resendUserEmailConfirmation = (id: string) =>
    adminAxios.post(`/admin/users/${id}/resend-confirmation`).then(res => res.data);

//reports
export const resolveReport = (reportId: string) =>
    adminAxios.post(`/admin/users/reports/${reportId}/resolve`).then(res => res.data);

// Admin Categories
export const createCategory = (model: CreateCategoryDto) => adminAxios.post<CategoryDto>('/admin/categories', model).then(res => res.data);

export const updateCategory = (id: number, model: UpdateCategoryDto) => adminAxios.put<CategoryDto>(`/admin/categories/${id}`, model).then(res => res.data);

export const deleteCategory = (id: number) => adminAxios.delete(`/admin/categories/${id}`).then(() => { });

// Admin Specifications
export const getAllSpecifications = () => adminAxios.get<SpecificationDto[]>('/admin/specifications').then(res => res.data);

export const createSpecification = (model: CreateSpecificationDto) => adminAxios.post<SpecificationDto>('/admin/specifications', model).then(res => res.data);

export const updateSpecification = (id: number, model: UpdateSpecificationDto) => adminAxios.put<SpecificationDto>(`/admin/specifications/${id}`, model).then(res => res.data);

export const deleteSpecification = (id: number) => adminAxios.delete(`/admin/specifications/${id}`).then(() => { });

export const addSpecToCategory = (specId: number, catId: number) => adminAxios.post<SpecificationDto>(`/admin/specifications/${specId}/categories/${catId}`).then(res => res.data);

export const removeSpecFromCategory = (specId: number, catId: number) => adminAxios.delete<SpecificationDto>(`/admin/specifications/${specId}/categories/${catId}`).then(res => res.data);

export const getAllAdminCategories = () => adminAxios.get<CategoryDto[]>('/admin/categories').then(res => res.data);

export const getAdminSpecificationsForCategories = (categoryNames: string[]) =>
    adminAxios.post<SpecificationDto[]>('/admin/specifications/for-categories', { categoryNames }).then(res => res.data);
// Admin Checklist Templates
export const getAllAdminChecklistTemplates = () => adminAxios.get<AdminChecklistTemplate[]>('/admin/checklist-templates').then(res => res.data);
export const getAllAdminChecklistPhases = () =>
    adminAxios.get<AdminChecklistPhaseDto[]>('/admin/checklist-templates/checklist-phases').then(res => res.data);
export const getAdminChecklistTemplateById = (id: number) => adminAxios.get<AdminChecklistTemplate>(`/admin/checklist-templates/${id}`).then(res => res.data);
export const AdmincreateChecklistTemplateAdmin = (model: AdminCreateChecklistTemplateDto) =>
    adminAxios.post<AdminChecklistTemplate>('/admin/checklist-templates', model).then(res => res.data);
export const AdminupdateChecklistTemplateAdmin = (id: number, model: AdminUpdateChecklistTemplateDto) =>
    adminAxios.put<AdminChecklistTemplate>(`/admin/checklist-templates/${id}`, model).then(res => res.data); export const AdmindeleteChecklistTemplate = (id: number) => adminAxios.delete(`/admin/checklist-templates/${id}`).then(() => { });
// Admin Checklist Templates with phases
//admin Shopify status ping
export const AdminSubscribeShopifyWebhooks = async (): Promise<ShopifyWebhookStatus> => {
    try {
        const response = await adminAxios.post('/admin/shopify/webhooks/subscribe', {}); // relative path only

        return response.data;   // axios already parses JSON
    } catch (error) {
        console.error('Shopify webhook subscribe error:', error || error);
        throw new Error(
            error + 'Failed to check/subscribe Shopify webhooks'
        );
    }
};
// Admin Translations
// Admin Languages
export const getAllLanguagesAdmin = () =>
    adminAxios.get<LanguageAdminDto[]>('/admin/languages').then(res => res.data);

export const createLanguageAdmin = (model: LanguageAdminDto) =>
    adminAxios.post<LanguageAdminDto>('/admin/languages', model).then(res => res.data);

export const updateLanguageAdmin = (code: string, model: LanguageAdminDto) =>
    adminAxios.put(`/admin/languages/${code}`, model).then(() => { });

export const deleteLanguageAdmin = (code: string) =>
    adminAxios.delete(`/admin/languages/${code}`).then(() => { });

// Admin Translations (new structure)
export const getAllAdminTranslations = () =>
    adminAxios.get<TranslationAdminDto[]>('/admin/translations').then(res => res.data);

export const getAdminTranslationById = (id: number) =>
    adminAxios.get<TranslationAdminDto>(`/admin/translations/${id}`).then(res => res.data);

export const createTranslationAdmin = (model: CreateTranslationDto) =>
    adminAxios.post<number>('/admin/translations', model).then(res => res.data);

export const updateTranslationAdmin = (id: number, model: UpdateTranslationDto) =>
    adminAxios.put(`/admin/translations/${id}`, model).then(() => { });

export const deleteTranslationAdmin = (id: number) =>
    adminAxios.delete(`/admin/translations/${id}`).then(() => { });

export const AdminSyncChecklists = async () => {
    return await adminAxios.post('/admin/checklist-templates/sync');
};

export const updateChecklistPhaseIcon = (id: number, icon: string | null) =>
    adminAxios.put<AdminChecklistPhaseDto>(`/admin/checklist-templates/phases/${id}`, { icon }).then(res => res.data);

// Admin My Journey (MyRebaby) visual designer — separate from shop children API
export const getAdminMyRebabyPhases = () =>
    adminAxios.get<AdminMyRebabyPhaseListDto[]>('/admin/myrebaby/phases').then(res => res.data);

export const getAdminMyRebabyPhase = (phaseId: number) =>
    adminAxios.get<AdminMyRebabyPhaseDetailDto>(`/admin/myrebaby/phases/${phaseId}`).then(res => res.data);

export const updateAdminMyRebabyPhase = (phaseId: number, dto: AdminUpdateMyRebabyPhaseContentDto) =>
    adminAxios.put<AdminMyRebabyPhaseDetailDto>(`/admin/myrebaby/phases/${phaseId}`, dto).then(res => res.data);

// Admin Sidebar Filters
export const getAllAdminSidebarFilters = () => adminAxios.get<AdminSidebarFilterListDto[]>('/admin/sidebar-filters').then(res => res.data);
export const getAdminSidebarFilterById = (id: number) => adminAxios.get<SidebarFilterDto>(`/admin/sidebar-filters/${id}`).then(res => res.data);
export const createAdminSidebarFilter = (dto: CreateSidebarFilterDto) => adminAxios.post<SidebarFilterDto>('/admin/sidebar-filters', dto).then(res => res.data);
export const updateAdminSidebarFilter = (id: number, dto: UpdateSidebarFilterDto) => adminAxios.put<SidebarFilterDto>(`/admin/sidebar-filters/${id}`, dto).then(res => res.data);
export const deleteAdminSidebarFilter = (id: number) => adminAxios.delete(`/admin/sidebar-filters/${id}`).then(() => { });
export const syncAdminSidebarFilterOptions = (id: number) => adminAxios.post<SidebarFilterDto>(`/admin/sidebar-filters/${id}/sync-options`).then(res => res.data);

export const getAllPartnerInquiries = () => adminAxios.get<any[]>('/admin/partner-inquiries').then(res => res.data);

// Support tickets (order issues + helpdesk)
export const getAllAdminTickets = () =>
    adminAxios.get<AdminTicketListDto[]>('/admin/tickets').then(res => res.data);

export const getAdminTicketById = (id: number) =>
    adminAxios.get<AdminTicketListDto>(`/admin/tickets/${id}`).then(res => res.data);

export const updateAdminTicketStatus = (id: number, status: string) =>
    adminAxios.put(`/admin/tickets/${id}/status`, { status }).then(() => {});

// Admin email templates (platform transactional mail only — not Shopify/SendCloud)
export interface AdminEmailTemplateListDto {
    id: number;
    templateKey: string;
    name: string;
    description?: string | null;
    category: string;
    isSystem: boolean;
    isActive: boolean;
    locales: string[];
    updatedAt: string;
}

export interface AdminEmailTemplateVersionDto {
    id: number;
    locale: string;
    subjectTemplate: string;
    htmlBodyTemplate: string;
    textBodyTemplate: string;
    isPublished: boolean;
    versionNumber: number;
    updatedAt: string;
}

export interface AdminEmailDeliveryPolicyDto {
    aggregateWindowValue?: number | null;
    aggregateWindowUnit?: string | null;
    eligibilityDelayValue?: number | null;
    eligibilityDelayUnit?: string | null;
    quietHoursStartLocal?: string | null;
    quietHoursEndLocal?: string | null;
    timeZoneId?: string | null;
    showAggregateWindow?: boolean;
    showEligibilityDelay?: boolean;
}

export interface AdminEmailTemplateDetailDto {
    id: number;
    templateKey: string;
    name: string;
    description?: string | null;
    category: string;
    isSystem: boolean;
    isActive: boolean;
    hasDeliveryPolicy?: boolean;
    deliveryPolicy?: AdminEmailDeliveryPolicyDto | null;
    versions: AdminEmailTemplateVersionDto[];
}

export interface AdminEmailPreviewResultDto {
    templateKey: string;
    locale: string;
    subject: string;
    htmlBody: string;
    textBody: string;
}

export interface AdminEmailOutboxListDto {
    id: number;
    toEmail: string;
    templateKey: string;
    locale: string;
    subject: string;
    status: string;
    attempts: number;
    lastError?: string | null;
    createdAt: string;
    sentAt?: string | null;
    nextAttemptAt: string;
}

export const getAllAdminEmailTemplates = () =>
    adminAxios.get<AdminEmailTemplateListDto[]>('/admin/email-templates').then(res => res.data);

export const getAdminEmailTemplate = (key: string) =>
    adminAxios.get<AdminEmailTemplateDetailDto>(`/admin/email-templates/${encodeURIComponent(key)}`).then(res => res.data);

export const updateAdminEmailTemplateLocale = (
    key: string,
    locale: string,
    dto: { subjectTemplate: string; htmlBodyTemplate: string; textBodyTemplate: string }
) =>
    adminAxios
        .put<AdminEmailTemplateVersionDto>(
            `/admin/email-templates/${encodeURIComponent(key)}/locales/${encodeURIComponent(locale)}`,
            dto
        )
        .then(res => res.data);

export const updateAdminEmailDeliveryPolicy = (key: string, dto: AdminEmailDeliveryPolicyDto) =>
    adminAxios
        .put<AdminEmailDeliveryPolicyDto>(
            `/admin/email-templates/${encodeURIComponent(key)}/delivery-policy`,
            dto
        )
        .then((res) => res.data);

export const previewAdminEmailTemplate = (
    key: string,
    body: { locale?: string; placeholders?: Record<string, string> }
) =>
    adminAxios
        .post<AdminEmailPreviewResultDto>(`/admin/email-templates/${encodeURIComponent(key)}/preview`, body)
        .then(res => res.data);

export const testSendAdminEmailTemplate = (
    key: string,
    body: { toEmail: string; locale?: string; placeholders?: Record<string, string> }
) =>
    adminAxios
        .post<{ outboxId?: number; message: string }>(
            `/admin/email-templates/${encodeURIComponent(key)}/test-send`,
            body
        )
        .then(res => res.data);

/** Global redirect of all transactional email (admin QA). */
export type AdminEmailTestModeDto = {
    enabled: boolean;
    toEmail?: string | null;
};

export const getAdminEmailTestMode = () =>
    adminAxios.get<AdminEmailTestModeDto>('/admin/email-templates/test-mode').then(res => res.data);

export const updateAdminEmailTestMode = (dto: AdminEmailTestModeDto) =>
    adminAxios.put<AdminEmailTestModeDto>('/admin/email-templates/test-mode', dto).then(res => res.data);

export const getAdminEmailOutbox = (status?: string, take = 50) => {
    const params: { take: number; status?: string } = { take };
    if (status && status.trim()) params.status = status.trim();
    return adminAxios
        .get<AdminEmailOutboxListDto[]>('/admin/email-outbox', { params })
        .then(res => res.data);
};

// Meta ad catalog (fixed product slots for public Meta feed)
export interface AdminMetaAdProductCardDto {
    id: number;
    title: string;
    price: number;
    imageUrl?: string | null;
    condition: string;
    status: string;
    isSold: boolean;
    sellerName?: string | null;
    categories: string[];
    viewCount: number;
}

export interface AdminMetaAdSlotDto {
    slotIndex: number;
    productId?: number | null;
    product?: AdminMetaAdProductCardDto | null;
    isInvalid: boolean;
    invalidReason?: string | null;
}

export interface AdminMetaAdCatalogDto {
    slotCount: number;
    slots: AdminMetaAdSlotDto[];
    updatedAt?: string | null;
}

export interface AdminMetaAdCatalogSaveDto {
    slotCount: number;
    slots: { slotIndex: number; productId?: number | null }[];
}

export const getAdminMetaAdCatalog = () =>
    adminAxios.get<AdminMetaAdCatalogDto>('/admin/meta-ad-catalog').then(res => res.data);

export const saveAdminMetaAdCatalog = (dto: AdminMetaAdCatalogSaveDto) =>
    adminAxios.put<AdminMetaAdCatalogDto>('/admin/meta-ad-catalog', dto).then(res => res.data);

export const getAdminMetaAdSuggestions = (limit = 12) =>
    adminAxios
        .get<AdminMetaAdProductCardDto[]>('/admin/meta-ad-catalog/suggestions', { params: { limit } })
        .then(res => res.data);

export const searchAdminMetaAdProducts = (params: {
    q?: string;
    category?: string;
    page?: number;
    limit?: number;
}) =>
    adminAxios
        .get<{ items: AdminMetaAdProductCardDto[]; total: number; page: number; limit: number }>(
            '/admin/meta-ad-catalog/products',
            { params }
        )
        .then(res => res.data);

// --- Platform fees (admin-only; % bands never on public shop APIs) ---
export const getPlatformFeeTiers = () =>
    adminAxios.get<PlatformFeeTierDto[]>('/admin/platform-fees/tiers').then((res) => res.data);

export const createPlatformFeeTier = (data: UpsertPlatformFeeTierDto) =>
    adminAxios.post<PlatformFeeTierDto>('/admin/platform-fees/tiers', data).then((res) => res.data);

export const updatePlatformFeeTier = (id: number, data: UpsertPlatformFeeTierDto) =>
    adminAxios.put<PlatformFeeTierDto>(`/admin/platform-fees/tiers/${id}`, data).then((res) => res.data);

export const deletePlatformFeeTier = (id: number) =>
    adminAxios.delete(`/admin/platform-fees/tiers/${id}`).then(() => undefined);

export const getPlatformFeeRevenue = () =>
    adminAxios.get<PlatformFeeRevenueDto>('/admin/platform-fees/revenue').then((res) => res.data);

export const previewPlatformFee = (itemPrice: number) =>
    adminAxios
        .get<PlatformFeePreviewDto>('/admin/platform-fees/preview', { params: { itemPrice } })
        .then((res) => res.data);

// --- Shipping size bands (buyer fee + Sendcloud method per package size) ---
export const getShippingSizeBandsAdmin = () =>
    adminAxios
        .get<import('../types').ShippingSizeBandAdminDto[]>('/admin/shipping-size-bands')
        .then((res) => res.data);

export const createShippingSizeBand = (data: import('../types').UpsertShippingSizeBandDto) =>
    adminAxios
        .post<import('../types').ShippingSizeBandAdminDto>('/admin/shipping-size-bands', data)
        .then((res) => res.data);

export const updateShippingSizeBand = (id: number, data: import('../types').UpsertShippingSizeBandDto) =>
    adminAxios
        .put<import('../types').ShippingSizeBandAdminDto>(`/admin/shipping-size-bands/${id}`, data)
        .then((res) => res.data);

export const deleteShippingSizeBand = (id: number) =>
    adminAxios.delete(`/admin/shipping-size-bands/${id}`).then(() => undefined);